import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { useSessionStore } from "@/features/tutor/store/useSessionStore";
import { ScratchPadCanvas, ScratchPadCanvasHandle } from "./ScratchPadCanvas";
import { useScratchpadChipStore } from "@/features/tutor/store/useScratchpadChipStore";

const THUMBNAIL_DEBOUNCE_MS = 400;
import { ScratchPadToolbar } from "./ScratchPadToolbar";
import { useScratchPad } from "./useScratchPad";
import { clearScratchPadSession, removeScratchPadState } from "./storage";
import { useInactivityTimer } from "@/features/tutor/hooks/useInactivityTimer";
import { SCRATCHPAD_PANEL_PROPS } from "@/drawlock/surfaceIds";

interface ScratchPadPanelProps {
  onPendingWorkChange?: (hasPending: boolean) => void;
  submitRef?: MutableRefObject<(() => Promise<void>) | null>;
  resetPendingRef?: MutableRefObject<(() => void) | null>;
  canvasExportRef?: MutableRefObject<(() => Promise<string | null>) | null>;
  imageItemsRef?: MutableRefObject<
    readonly import("./types").ScratchPadImageItem[] | null
  >;
  clearImageBytesRef?: MutableRefObject<(() => void) | null>;
  removeImageByIdRef?: MutableRefObject<((id: string) => void) | null>;
  onInteract?: () => void;
  onCameraClick?: () => void;
  pendingImageB64?: string | null;
  onImagePlaced?: () => void;
  onImageCountChange?: (count: number) => void;
}

export const ScratchPadPanel = ({
  onPendingWorkChange,
  submitRef,
  resetPendingRef,
  canvasExportRef,
  imageItemsRef,
  clearImageBytesRef,
  removeImageByIdRef,
  onInteract,
  onCameraClick,
  pendingImageB64,
  onImagePlaced,
  onImageCountChange,
}: ScratchPadPanelProps = {}) => {
  const slideIndex = useSlideStore((s) => s.currentSlideIndex);
  const sessionId = useSessionStore((s) => s.sessionId);
  const { resetAndRestartInactivityTimer } = useInactivityTimer();
  const canvasRef = useRef<ScratchPadCanvasHandle | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const lastSessionIdRef = useRef<string | null>(null);
  const lastSlideIndexRef = useRef<number | string | null>(null);
  const [lastSubmittedKey, setLastSubmittedKey] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [expandedImageId, setExpandedImageId] = useState<string | null>(null);
  const {
    mode,
    setMode,
    tool,
    setTool,
    penColor,
    setPenColor,
    penSize,
    setPenSize,
    eraserSize,
    setEraserSize,
    strokes,
    pushStroke,
    removeStrokes,
    undo,
    redo,
    clear,
    textItems,
    addTextItem,
    updateTextItem,
    moveTextItem,
    removeTextItem,
    activeTextId,
    setActiveTextId,
    imageItems,
    addImageItem,
    removeImageItem,
    clearImageBytes,
    strokeStyle,
    penStrokeStyle,
  } = useScratchPad({ sessionId, slideIndex });

  const prevPendingImageB64Ref = useRef<string | null | undefined>(null);

  const snapshotKey = useMemo(
    () => JSON.stringify({ strokes, textItems, imageItems }),
    [strokes, textItems, imageItems],
  );

  useEffect(() => {
    if (lastSubmittedKey == null) setLastSubmittedKey(snapshotKey);
  }, [lastSubmittedKey, snapshotKey]);

  useEffect(() => {
    if (!sessionId) return;
    if (lastSessionIdRef.current !== sessionId) {
      clearScratchPadSession(sessionId);
      lastSessionIdRef.current = sessionId;
      lastSlideIndexRef.current = null;
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId || slideIndex == null) return;
    if (
      lastSlideIndexRef.current != null &&
      lastSlideIndexRef.current !== slideIndex
    ) {
      removeScratchPadState(sessionId, lastSlideIndexRef.current);
    }
    lastSlideIndexRef.current = slideIndex;
    clear();
    setLastSubmittedKey(null);
  }, [sessionId, slideIndex, clear]);

  const onDrawingIdle = useCallback(() => {
    // Reset and restart inactivity timer when student draws (called after
    // 700ms of no drawing activity)
    resetAndRestartInactivityTimer();
  }, [resetAndRestartInactivityTimer]);

  // Wrap updateTextItem to reset inactivity timer when student types in text box
  const handleTextChange = useCallback(
    (id: string, text: string) => {
      updateTextItem(id, text);
      resetAndRestartInactivityTimer();
    },
    [updateTextItem, resetAndRestartInactivityTimer],
  );

  // When a new image arrives, add it immediately as an attachment
  useEffect(() => {
    if (pendingImageB64 && pendingImageB64 !== prevPendingImageB64Ref.current) {
      prevPendingImageB64Ref.current = pendingImageB64;
      addImageItem(`data:image/jpeg;base64,${pendingImageB64}`);
      onImagePlaced?.();
    }
  }, [pendingImageB64, addImageItem, onImagePlaced]);

  const onSelectTextMode = () => {
    if (textItems.length === 0) {
      addTextItem();
      return;
    }
    setActiveTextId(textItems[0].id);
    setMode("text");
  };

  const hasContent =
    strokes.some((stroke) => stroke.points.length > 1) ||
    textItems.some((item) => item.text.trim() !== "") ||
    imageItems.length > 0;
  const hasChangedSinceLastSubmit = snapshotKey !== lastSubmittedKey;
  const showCheckAnswer = hasContent && hasChangedSinceLastSubmit;

  // Notify parent when pending state changes
  useEffect(() => {
    onPendingWorkChange?.(showCheckAnswer);
  }, [showCheckAnswer, onPendingWorkChange]);

  // Expose stable refs to parent (submitRef is always null — placeholder)
  useEffect(() => {
    if (submitRef) submitRef.current = null;
    if (canvasExportRef)
      canvasExportRef.current = () =>
        canvasRef.current?.exportPngDataUrl() ?? Promise.resolve(null);
  }, [submitRef, canvasExportRef]);

  useEffect(() => {
    if (imageItemsRef) imageItemsRef.current = imageItems;
    onImageCountChange?.(imageItems.length);
  }, [imageItems, imageItemsRef, onImageCountChange]);

  useEffect(() => {
    if (clearImageBytesRef) clearImageBytesRef.current = clearImageBytes;
  }, [clearImageBytes, clearImageBytesRef]);

  useEffect(() => {
    if (removeImageByIdRef) removeImageByIdRef.current = removeImageItem;
  }, [removeImageItem, removeImageByIdRef]);

  // resetPendingRef closure must capture latest snapshotKey
  useEffect(() => {
    if (resetPendingRef)
      resetPendingRef.current = () => setLastSubmittedKey(snapshotKey);
  }, [snapshotKey, resetPendingRef]);

  // snapshotKey covers strokes, text and images, so the chip follows an erase
  // or an undo too — onInteract only fires on a commit or a text edit.
  const lastThumbnailKeyRef = useRef<string | null>(null);
  // Subscribed, not read through getState: losing pending work clears the
  // thumbnail without touching the snapshot key, and an effect that does not
  // re-run for that never reaches the guard below — leaving the chip on blank
  // paper for a page that has a drawing. A boolean, so a new picture for the
  // same page does not re-render the panel.
  const hasThumbnail = useScratchpadChipStore((s) => s.thumbnail !== null);
  useEffect(() => {
    if (!hasContent) return undefined;
    // Repainting and re-encoding for a key already rendered would put a JPEG
    // encode on the inking thread for an identical picture — but only while
    // that picture is still on the chip.
    if (lastThumbnailKeyRef.current === snapshotKey && hasThumbnail) {
      return undefined;
    }
    const timer = setTimeout(async () => {
      const dataUrl = await canvasRef.current?.exportThumbnailDataUrl();
      if (!dataUrl) return;
      lastThumbnailKeyRef.current = snapshotKey;
      useScratchpadChipStore.getState().setThumbnail(dataUrl);
    }, THUMBNAIL_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [snapshotKey, hasContent, hasThumbnail]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    setIsScrolled(container.scrollTop > 20);
  }, []);

  const scrollToTop = useCallback(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return (
    <div
      {...SCRATCHPAD_PANEL_PROPS}
      className="w-full h-full flex flex-col rounded-[18px] shadow-xl overflow-hidden min-h-0 bg-white"
    >
      <div className="bg-[#02734A] text-white px-4 py-[20px]">
        <div className="text-center font-shantell text-[19px] font-semibold tracking-[0.2px]">
          Scratch pad
        </div>
      </div>

      <div className="bg-white px-4 py-2 flex items-center justify-center">
        <ScratchPadToolbar
          mode={mode}
          setMode={setMode}
          tool={tool}
          setTool={setTool}
          undo={undo}
          redo={redo}
          clear={clear}
          penColor={penColor}
          setPenColor={setPenColor}
          penSize={penSize}
          setPenSize={setPenSize}
          eraserSize={eraserSize}
          setEraserSize={setEraserSize}
          onSelectTextMode={onSelectTextMode}
          onCameraClick={onCameraClick}
        />
      </div>

      <div className="flex-1 min-h-0 flex flex-col relative">
        <div
          ref={scrollContainerRef}
          className="flex-1 min-h-0 overflow-y-auto scratchpad-paper"
          style={imageItems.length > 0 ? { paddingBottom: 80 } : undefined}
        >
          <ScratchPadCanvas
            ref={canvasRef}
            strokes={strokes}
            strokeStyle={strokeStyle}
            penStrokeStyle={penStrokeStyle}
            onStrokeStart={() =>
              useScratchpadChipStore.getState().markStrokeStarted()
            }
            onStrokeDiscarded={() =>
              useScratchpadChipStore.getState().clearStrokeStarted()
            }
            onCommitStroke={(stroke) => {
              pushStroke(stroke);
              onInteract?.();
            }}
            onEraseStrokes={removeStrokes}
            onDrawingIdle={onDrawingIdle}
            mode={mode}
            textItems={textItems}
            activeTextId={activeTextId}
            onChangeText={(id, text) => {
              handleTextChange(id, text);
              onInteract?.();
            }}
            onMoveText={moveTextItem}
            onActivateText={setActiveTextId}
            onRemoveText={removeTextItem}
            virtualHeightMultiplier={2}
          />
          <div className="sticky bottom-4 left-0 right-0 flex flex-col items-center justify-center gap-2 pointer-events-none">
            {isScrolled && (
              <button
                type="button"
                onClick={scrollToTop}
                className="pointer-events-auto flex items-center justify-center gap-[5px] p-[10px] h-[33px] bg-white border border-[#1C9C6D] rounded-[5px] font-shantell text-[10px] font-bold leading-none tracking-normal text-[#1C9C6D] hover:bg-gray-50 transition-colors"
              >
                <svg
                  width="9"
                  height="8"
                  viewBox="0 0 9 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.858095 4.48404L4.25693 1.0852L7.65577 4.48404C7.75289 4.57784 7.88298 4.62975 8.018 4.62858C8.15303 4.6274 8.28219 4.57324 8.37767 4.47776C8.47315 4.38228 8.52731 4.25312 8.52848 4.1181C8.52966 3.98307 8.47775 3.85299 8.38394 3.75586L4.88932 0.260727C4.72201 0.0938052 4.49533 6.13877e-05 4.25899 6.14084e-05C4.02265 6.14291e-05 3.79597 0.0938053 3.62866 0.260727L0.13404 3.75586C0.0493857 3.85414 0.00493591 3.98074 0.00957078 4.11036C0.0142056 4.23999 0.0675847 4.36309 0.159042 4.45507C0.250499 4.54705 0.373297 4.60112 0.502894 4.60649C0.632491 4.61186 0.759341 4.56813 0.858095 4.48404Z"
                    fill="#1C9C6D"
                  />
                  <path
                    d="M4.88681 3.61941C4.7195 3.45249 4.49282 3.35874 4.25648 3.35874C4.02015 3.35874 3.79346 3.45249 3.62615 3.61941L0.13153 7.11454C0.0436875 7.21253 -0.00331147 7.34043 0.000181851 7.47197C0.00367613 7.60352 0.0573985 7.72874 0.150318 7.82193C0.243238 7.91511 0.368307 7.96919 0.499845 7.97305C0.631384 7.97692 0.759412 7.93028 0.857645 7.84272L4.25648 4.44388L7.65532 7.84272C7.70282 7.8919 7.75965 7.93114 7.82248 7.95813C7.88531 7.98512 7.95288 7.99932 8.02126 7.99992C8.08964 8.00051 8.15745 7.98748 8.22074 7.96159C8.28403 7.93569 8.34152 7.89745 8.38988 7.8491C8.43823 7.80075 8.47647 7.74325 8.50236 7.67996C8.52825 7.61668 8.54128 7.54886 8.54069 7.48048C8.54009 7.41211 8.52589 7.34453 8.4989 7.2817C8.47191 7.21887 8.43268 7.16205 8.38349 7.11454L4.88681 3.61941Z"
                    fill="#1C9C6D"
                  />
                </svg>
                Scroll to the top
              </button>
            )}
          </div>
        </div>
        {imageItems.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 bg-none mx-2 px-0 py-2 mt-1">
            <div className="flex items-end gap-2 overflow-x-auto overflow-y-visible scrollbar-hidden pt-2">
              {imageItems.map((item) => {
                const isExpanded = expandedImageId === item.id;
                return (
                  <div key={item.id} className="relative flex-shrink-0">
                    <div
                      className="rounded-lg border-2 border-[#1C6E4A] overflow-hidden bg-[#E8F5EF] transition-all duration-200"
                      style={{
                        width: isExpanded ? 160 : 56,
                        height: isExpanded ? 160 : 56,
                      }}
                    >
                      {item.dataUrl ? (
                        <img
                          src={item.dataUrl}
                          alt={item.name}
                          draggable={false}
                          className="w-full h-full object-cover cursor-pointer"
                          onClick={() =>
                            setExpandedImageId(isExpanded ? null : item.id)
                          }
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            aria-hidden="true"
                          >
                            <rect
                              x="3"
                              y="3"
                              width="18"
                              height="18"
                              rx="3"
                              stroke="#1C6E4A"
                              strokeWidth="1.8"
                              fill="none"
                            />
                            <circle cx="8.5" cy="8.5" r="1.5" fill="#1C6E4A" />
                            <path
                              d="M3 15l5-5 4 4 3-3 6 6"
                              stroke="#1C6E4A"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      aria-label="Remove image"
                      className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-slate-600 text-xs text-white cursor-pointer p-0 leading-none"
                      onClick={() => removeImageItem(item.id)}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
